import { useEffect, useState } from 'react';
import _ from 'lodash';
import { Api } from 'services/api';

import { InvitationStatus, NewUser, OrganizationUserStatus } from '../../../models';
import { Form } from 'antd';
import { DisableStaff, EnableStaff, GetAllUsers, GetReferrals, GetRoles, InviteStaff, OrganizationInviteDto, StaffUpdateDto, UpdateStaff } from 'services/organization.service';
import { IRole, OrganizationReferral } from 'models/organization';
import moment from 'moment';
import { useCurrentOrganization } from 'hooks';

type OrganizationUsersProps = {
  idOrganization: string;
  defaultFilters?: DefaultFilters;
  pageNumber?: number;
  pageSize?: number;
  text?: string;
};

export interface DefaultFilters {
  search?: string;
}

export const defaultFilters: DefaultFilters = {
  search: '',
};

export function mapOrganizationUser(item: NewUser) {
  return {
    ...item,
    status: item.isDisabled ? OrganizationUserStatus.Disabled : item.invitationStatus === InvitationStatus.Pending ? OrganizationUserStatus.Invited : OrganizationUserStatus.Active,
    disabledAt: item.disabledAt ? moment(item.disabledAt) : undefined,
  };
}

export function mapOrganizationReferral(item: OrganizationReferral) {
  return {
    ...item,
    createdAt: moment(item.createdAt),
  };
}

export function useOrganizationUsers(props: OrganizationUsersProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [data, setData] = useState<NewUser[]>();
  const [total, setTotal] = useState<number>();
  const [pagination, setPagination] = useState({ pageSize: 100, pageNumber: 1 });
  const [filterCounter, setFilterCounter] = useState(0);
  const [filter, setFilter] = useState<DefaultFilters>({
    search: props.defaultFilters?.search ?? defaultFilters.search,
  });

  const load = async () => {
    const filters = {
      pageNumber: props.pageNumber ?? pagination.pageNumber,
      pageSize: props.pageSize ?? pagination.pageSize,
      text: props.text ?? filter.search,
    };

    const diffSearch = !_.isEqual(defaultFilters.search, filters.text);

    const counter = [diffSearch].filter(Boolean).length;

    setFilterCounter(counter);

    setLoading(true);
    await Api.tryCatch(
      GetAllUsers(props.idOrganization, filters, { cancelToken: cancelToken.token }),
      (rs) => {
        setLoading(false);
        setData(rs.data.map(mapOrganizationUser));
        setTotal(rs.meta.total);
      },
      (err) => {
        setLoading(false);
        setError(err.message);
      },
    );
  };

  let cancelToken = Api.getCancelTokenSource();
  useEffect(() => {
    cancelToken = Api.getCancelTokenSource();
    return () => {
      cancelToken.cancel();
    };
  }, []);

  useEffect(() => {
    load();
  }, [props.pageNumber, props.pageSize, props.text, filter, pagination]);

  return { loading, error, data, total, pagination, setPagination, filterCounter, setFilter, reloadList: load };
}

export function useSaveStaff() {
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();

  const update = async (organizationId: string, userId: string, payload: StaffUpdateDto) => {
    setIsSaving(true);
    try {
      return await UpdateStaff(organizationId, userId, payload);
    } finally {
      setIsSaving(false);
    }
  };

  const invite = async (organizationId: string, payload: OrganizationInviteDto) => {
    setIsSaving(true);
    try {
      return await InviteStaff(organizationId, payload);
    } finally {
      setIsSaving(false);
    }
  };

  const disable = async (organizationId: string, userId: string) => {
    setIsSaving(true);
    try {
      return await DisableStaff(organizationId, userId);
    } finally {
      setIsSaving(false);
    }
  };

  const enable = async (organizationId: string, userId: string) => {
    setIsSaving(true);
    try {
      return await EnableStaff(organizationId, userId);
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaving, invite, update, disable, enable, form };
}

export function mapRole(item: IRole) {
  return {
    ...item,
    createdAt: moment(item.createdAt),
    updatedAt: moment(item.updatedAt),
  };
}

export function useReferrals(props: OrganizationUsersProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [data, setData] = useState<OrganizationReferral[]>();
  const [total, setTotal] = useState<number>();
  const [pagination, setPagination] = useState({ pageSize: 100, pageNumber: 1 });
  const [filterCounter, setFilterCounter] = useState(0);
  const [filter, setFilter] = useState<DefaultFilters>({
    search: props.defaultFilters?.search ?? defaultFilters.search,
  });

  const load = async () => {
    const filters = {
      pageNumber: props.pageNumber ?? pagination.pageNumber,
      pageSize: props.pageSize ?? pagination.pageSize,
      text: props.text ?? filter.search,
    };

    const diffSearch = !_.isEqual(defaultFilters.search, filters.text);

    const counter = [diffSearch].filter(Boolean).length;

    setFilterCounter(counter);

    setLoading(true);
    await Api.tryCatch(
      GetReferrals(props.idOrganization, filters, { cancelToken: cancelToken.token }),
      (rs) => {
        setLoading(false);
        setData(rs.data.map(mapOrganizationReferral));
        setTotal(rs.meta.total);
      },
      (err) => {
        setLoading(false);
        setError(err.message);
      },
    );
  };

  let cancelToken = Api.getCancelTokenSource();
  useEffect(() => {
    cancelToken = Api.getCancelTokenSource();
    return () => {
      cancelToken.cancel();
    };
  }, []);

  useEffect(() => {
    load();
  }, [props.pageNumber, props.pageSize, props.text, filter, pagination]);

  return { loading, error, data, total, pagination, setPagination, filterCounter, setFilter, reloadList: load };
}

export function useRoles() {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [data, setData] = useState<IRole[]>();

  const organization = useCurrentOrganization();

  const load = async () => {
    setLoading(true);
    await Api.tryCatch(
      GetRoles(organization.id, { cancelToken: cancelToken.token }),
      (rs) => {
        setLoading(false);
        setData(rs.data.map(mapRole));
      },
      (err) => {
        setLoading(false);
        setError(err.message);
      },
    );
  };

  let cancelToken = Api.getCancelTokenSource();
  useEffect(() => {
    cancelToken = Api.getCancelTokenSource();
    return () => {
      cancelToken.cancel();
    };
  }, []);

  useEffect(() => {
    load();
  }, []);

  return { loading, error, data, reloadList: load };
}
